import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import styled, { createGlobalStyle, keyframes } from "styled-components";
import { connect } from "react-redux";
import { formatDistanceToNow } from "date-fns";
import { parseDate } from "../utils/helpers/date";
import {
  Globe,
  MapPin,
  Calendar,
  User,
  Briefcase,
  Navigation,
  Star,
  ArrowUpDown,
  Link2,
  Info,
} from "lucide-react";

import NavBar from "../components/layout/NavBar";
import BackButton from "../components/BackButton";
import { request } from "../services/networking/request";
import { API_CONSTANT_MAP } from "../utils/constants/endpoints";

/* ——— Theming ——— */
const theme = {
  colors: {
    primary: "hsl(217, 91%, 60%)",
    primaryLight: "hsl(217, 91%, 96%)",
    primaryText: "hsl(217, 91%, 50%)",
    textHeading: "#0f172a",
    textBody: "#334155",
    textMuted: "#64748b",
    background: "#f8fafc",
    surface: "#ffffff",
    border: "#e2e8f0",
  },
  shadows: {
    sm: "0 1px 2px rgba(15, 23, 42, 0.06)",
    md: "0 6px 20px rgba(15, 23, 42, 0.07)",
  },
  font: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  radius: "14px",
};

const GlobalStyle = createGlobalStyle`
  body { background: ${theme.colors.background}; font-family: ${theme.font}; }
`;

const fadeIn = keyframes`from{opacity:0}to{opacity:1}`;

/* ——— Layout ——— */
const Page = styled.div`
  min-height: 100vh;
  padding: 64px 0px;
`;
const Container = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: 20px;
  @media (max-width: 1024px) {
    padding: 0;
  }
`;

const HeaderWrap = styled.header`
  position: relative;
  margin: 0 0 14px;
  animation: ${fadeIn} 0.2s ease;
  background: linear-gradient(135deg, #fbfdff, #ffffff);
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radius};
  box-shadow: ${theme.shadows.sm};
  overflow: hidden;
  @media (max-width: 1024px) {
    border-radius: 0;
    border-left: 0;
    border-right: 0;
  }
`;

const Banner = styled.div`
  height: 120px;
  background: radial-gradient(
      1000px 300px at 20% -20%,
      rgba(59, 130, 246, 0.12),
      transparent
    ),
    radial-gradient(
      800px 300px at 80% -10%,
      rgba(168, 85, 247, 0.12),
      transparent
    );
`;

const HeaderInner = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 0 18px 16px;
  margin-top: -40px;
  @media (max-width: 560px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    margin-top: -30px;
  }
`;

const Avatar = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 999px;
  overflow: hidden;
  border: 3px solid #fff;
  box-shadow: 0 2px 10px rgba(15, 23, 42, 0.12);
  background: ${theme.colors.primaryLight};
  display: grid;
  place-items: center;
  color: ${theme.colors.primaryText};
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const Title = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
  flex: 1;
  h1 {
    margin: 0;
    font-weight: 900;
    letter-spacing: -0.01em;
    color: ${theme.colors.textHeading};
    font-size: 1.4rem;
  }
  .muted {
    color: ${theme.colors.textMuted};
    font-weight: 600;
    font-size: 0.92rem;
  }
`;

const Ghost = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #fff;
  color: ${theme.colors.textBody};
  border: 1px solid ${theme.colors.border};
  border-radius: 10px;
  padding: 9px 12px;
  font-weight: 700;
  cursor: pointer;
  &:hover {
    background: ${theme.colors.primaryLight};
    color: ${theme.colors.primaryText};
    border-color: ${theme.colors.primary};
  }
`;

const MetaRow = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  padding: 0 18px 16px;
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 999px;
  background: #f6f8fc;
  border: 1px solid #e8eef9;
  font-weight: 600;
  font-size: 0.8rem;
  color: ${theme.colors.textBody};
`;

/* ——— Tabs ——— */
const TabsBar = styled.div`
  display: flex;
  gap: 16px;
  padding: 10px 18px;
  border-top: 1px dashed ${theme.colors.border};
  background: #fff;
`;
const TabBtn = styled.button`
  background: none;
  border: 0;
  cursor: pointer;
  font-weight: 800;
  padding: 8px 0;
  position: relative;
  color: ${(p) =>
    p.active ? theme.colors.primaryText : theme.colors.textMuted};
  &:after {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    bottom: -2px;
    height: 2px;
    background: ${(p) => (p.active ? theme.colors.primaryText : "transparent")};
  }
`;

/* ——— Content ——— */
const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  margin-top: 10px;
`;

const Card = styled.div`
  background: #fff;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radius};
  box-shadow: ${theme.shadows.sm};
  padding: 16px;
  animation: ${fadeIn} 0.2s ease;
`;

const SectionTitle = styled.h3`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 10px;
  color: ${theme.colors.textHeading};
  font-size: 1.05rem;
  font-weight: 800;
`;

/* ——— Tasks list ——— */
const List = styled.div`
  display: grid;
  gap: 12px;
`;
const Item = styled(Link)`
  display: block;
  text-decoration: none;
  color: inherit;
  border: 1px solid ${theme.colors.border};
  border-radius: 14px;
  background: #fff;
  padding: 14px;
  transition: 0.12s;
  box-shadow: 0 1px 0 rgba(15, 23, 42, 0.02);
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 18px rgba(15, 23, 42, 0.06);
    border-color: #dbe3ef;
  }
`;
const ItemTop = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;
const ItemTitle = styled.div`
  font-weight: 800;
  color: ${theme.colors.textHeading};
`;
const Price = styled.div`
  margin-left: auto;
  font-weight: 900;
  color: ${theme.colors.primaryText};
`;
const ItemMeta = styled.div`
  margin-top: 8px;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  font-size: 0.82rem;
  color: ${theme.colors.textMuted};
`;
const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 999px;
  background: #f6f8fc;
  border: 1px solid #e8eef9;
  font-weight: 600;
  font-size: 0.8rem;
`;

/* ——— Small helpers ——— */
const Empty = styled.div`
  padding: 16px;
  text-align: center;
  color: ${theme.colors.textMuted};
  border: 1px dashed ${theme.colors.border};
  border-radius: 12px;
  background: #fafcff;
`;

const SortRow = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
  select {
    height: 38px;
    border: 1px solid ${theme.colors.border};
    border-radius: 10px;
    padding: 0 10px;
    font-weight: 600;
    &:focus {
      outline: none;
      border-color: ${theme.colors.primary};
      box-shadow: 0 0 0 3px ${theme.colors.primaryLight};
    }
  }
`;

const BackRow = styled.div`
  margin-bottom: 10px;
`;

const Skeleton = styled.div`
  border-radius: 12px;
  background: linear-gradient(90deg, #f1f5f9, #f8fafc, #f1f5f9);
  background-size: 200% 100%;
  animation: shimmer 1.2s infinite linear;
  height: ${(p) => p.h || "14px"};
  width: ${(p) => p.w || "100%"};

  @keyframes shimmer {
    from {
      background-position: 200% 0;
    }
    to {
      background-position: -200% 0;
    }
  }
`;

/* ——— Page ——— */
const UserProfilePage = ({ userState }) => {
  const { id } = useParams(); // poster user id
  const nav = useNavigate();

  const [profile, setProfile] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [completedTasks, setCompletedTasks] = useState([]);
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  const [isLoadingTasks, setIsLoadingTasks] = useState(true);
  const [isLoadingCompletedTasks, setIsLoadingCompletedTasks] = useState(true);

  const [tab, setTab] = useState("about"); // about | tasks | completed
  const [sortBy, setSortBy] = useState("date_desc"); // date_desc|date_asc|budget_desc|budget_asc
  const [completedSortBy, setCompletedSortBy] = useState("date_desc");

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      setIsLoadingProfile(true);
      try {
        const { data } = await request(
          "POST",
          API_CONSTANT_MAP.fetch_user_profile,
          { user_id: Number(id) }
        );
        if (mounted && data?.success) setProfile(data.user);
      } catch (e) {
        console.error("Failed to load profile", e);
      } finally {
        if (mounted) setIsLoadingProfile(false);
      }
    };

    load();
    return () => (mounted = false);
  }, [id]);

  useEffect(() => {
    let mounted = true;
    const loadTasks = async () => {
      setIsLoadingTasks(true);
      try {
        const { data } = await request(
          "POST",
          API_CONSTANT_MAP.fetch_user_profile_tasks,
          {
            user_id: Number(id),
            limit: 50,
          }
        );
        if (mounted && data?.success) {
          setTasks(data.tasks || []);
          setCompletedTasks(data.completed_tasks || []);
        }
      } catch (e) {
        console.error("Failed to load tasks", e);
      } finally {
        if (mounted) {
          setIsLoadingTasks(false);
          setIsLoadingCompletedTasks(false);
        }
      }
    };
    loadTasks();
    return () => (mounted = false);
  }, [id]);

  const fullName = useMemo(() => {
    if (!profile) return "";
    const first = profile.first_name || "";
    const last = profile.last_name || "";
    return [first, last].filter(Boolean).join(" ");
  }, [profile]);

  const getInitials = (profile) => {
    if (!profile) return "U";
    const first = profile.first_name || "";
    const last = profile.last_name || "";
    if (first && last) {
      return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
    }
    if (first) return first.charAt(0).toUpperCase();
    if (last) return last.charAt(0).toUpperCase();
    return "U";
  };

  const getAvatarColor = (userId) => {
    const colors = [
      "#3B82F6",
      "#EF4444",
      "#10B981",
      "#F59E0B",
      "#8B5CF6",
      "#EC4899",
      "#06B6D4",
      "#84CC16",
      "#F97316",
      "#6366F1",
    ];
    return colors[userId % colors.length];
  };

  // Sorting functions
  const sortTasks = (tasks, sortType) => {
    const sorted = [...tasks];
    switch (sortType) {
      case "date_desc":
        return sorted.sort(
          (a, b) => parseDate(b.created_at) - parseDate(a.created_at)
        );
      case "date_asc":
        return sorted.sort(
          (a, b) => parseDate(a.created_at) - parseDate(b.created_at)
        );
      case "budget_desc":
        return sorted.sort((a, b) => (b.budget || 0) - (a.budget || 0));
      case "budget_asc":
        return sorted.sort((a, b) => (a.budget || 0) - (b.budget || 0));
      default:
        return sorted;
    }
  };

  const sortCompletedTasks = (tasks, sortType) => {
    const sorted = [...tasks];
    switch (sortType) {
      case "date_desc":
        return sorted.sort(
          (a, b) =>
            parseDate(b.completed_at || b.updated_at) -
            parseDate(a.completed_at || a.updated_at)
        );
      case "date_asc":
        return sorted.sort(
          (a, b) =>
            parseDate(a.completed_at || a.updated_at) -
            parseDate(b.completed_at || b.updated_at)
        );
      default:
        return sorted;
    }
  };

  // Memoized sorted arrays
  const sortedTasks = useMemo(() => sortTasks(tasks, sortBy), [tasks, sortBy]);
  const sortedCompletedTasks = useMemo(
    () => sortCompletedTasks(completedTasks, completedSortBy),
    [completedTasks, completedSortBy]
  );

  return (
    <>
      <GlobalStyle />
      <Page>
        <NavBar user={userState.user} />

        <Container>
          {/* Back */}
          <BackRow>
            <BackButton onClick={() => nav(-1)} />
          </BackRow>

          {/* Header */}
          <HeaderWrap>
            <Banner />
            {isLoadingProfile ? (
              <>
                {/* Header Skeleton */}
                <HeaderInner>
                  <Avatar>
                    <Skeleton
                      h="80px"
                      w="80px"
                      style={{ borderRadius: "50%" }}
                    />
                  </Avatar>
                  <Title>
                    <Skeleton
                      h="28px"
                      w="200px"
                      style={{ marginBottom: "8px" }}
                    />
                    <Skeleton h="16px" w="150px" />
                  </Title>
                </HeaderInner>

                <MetaRow>
                  <Skeleton
                    h="28px"
                    w="80px"
                    style={{ borderRadius: "999px" }}
                  />
                  <Skeleton
                    h="28px"
                    w="90px"
                    style={{ borderRadius: "999px" }}
                  />
                  <Skeleton
                    h="28px"
                    w="120px"
                    style={{ borderRadius: "999px" }}
                  />
                </MetaRow>

                <TabsBar>
                  <Skeleton h="20px" w="50px" />
                  <Skeleton h="20px" w="100px" />
                </TabsBar>
              </>
            ) : (
              <>
                <HeaderInner>
                  <Avatar
                    style={
                      !profile?.avatar
                        ? {
                            backgroundColor: getAvatarColor(Number(id)),
                            color: "white",
                          }
                        : {}
                    }
                  >
                    {profile?.avatar ? (
                      <img 
                        src={profile.avatar} 
                        alt="avatar"
                        onError={(e) => {
                          console.error('❌ User profile avatar failed to load:', {
                            url: profile.avatar,
                            error: e,
                            timestamp: new Date().toISOString()
                          });
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'block';
                          // Also update the Avatar container to show colored background
                          e.target.parentElement.style.backgroundColor = getAvatarColor(Number(id));
                          e.target.parentElement.style.color = 'white';
                        }}
                        onLoad={() => {
                          console.log('✅ User profile avatar loaded successfully:', profile.avatar);
                        }}
                        style={{ display: 'block' }}
                        referrerPolicy="no-referrer"
                        crossOrigin="anonymous"
                      />
                    ) : null}
                    <span style={{ 
                      fontSize: "1.8rem", 
                      fontWeight: "700",
                      display: profile?.avatar ? 'none' : 'block'
                    }}>
                      {getInitials(profile)}
                    </span>
                  </Avatar>
                  <Title>
                    <h1>{fullName || "User"}</h1>
                    <div className="muted">
                      Member since{" "}
                      {profile?.created_at
                        ? formatDistanceToNow(parseDate(profile.created_at), {
                            addSuffix: true,
                          })
                        : "—"}
                    </div>
                  </Title>
                </HeaderInner>

                <MetaRow>
                  {profile?.zipcode && (
                    <Chip>
                      <MapPin size={14} /> ZIP {profile.zipcode}
                    </Chip>
                  )}
                  {profile?.website && (
                    <Chip
                      as="a"
                      href={profile.website}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <Globe size={14} /> Website
                    </Chip>
                  )}
                  <Chip>
                    <Calendar size={14} /> {sortedTasks.length} posted tasks
                  </Chip>
                </MetaRow>

                <TabsBar>
                  <TabBtn
                    active={tab === "about"}
                    onClick={() => setTab("about")}
                  >
                    About
                  </TabBtn>
                  <TabBtn
                    active={tab === "tasks"}
                    onClick={() => setTab("tasks")}
                  >
                    Posted Tasks
                  </TabBtn>
                  <TabBtn
                    active={tab === "completed"}
                    onClick={() => setTab("completed")}
                  >
                    Completed Work
                  </TabBtn>
                </TabsBar>
              </>
            )}
          </HeaderWrap>

          <ContentGrid>
            {/* About */}
            {tab === "about" && (
              <Card>
                <SectionTitle>
                  <User size={18} /> About {fullName || "this user"}
                </SectionTitle>
                {isLoadingProfile ? (
                  <div style={{ display: "grid", gap: 16 }}>
                    <div style={{ display: "grid", gap: 8 }}>
                      <Skeleton h="16px" w="100%" />
                      <Skeleton h="16px" w="95%" />
                      <Skeleton h="16px" w="88%" />
                      <Skeleton h="16px" w="75%" />
                    </div>

                    <div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          marginBottom: 12,
                        }}
                      >
                        <Skeleton h="18px" w="18px" />
                        <Skeleton h="18px" w="80px" />
                      </div>
                      <div
                        style={{ display: "flex", gap: 10, flexWrap: "wrap" }}
                      >
                        <Skeleton
                          h="28px"
                          w="120px"
                          style={{ borderRadius: "999px" }}
                        />
                        <Skeleton
                          h="28px"
                          w="140px"
                          style={{ borderRadius: "999px" }}
                        />
                        <Skeleton
                          h="28px"
                          w="100px"
                          style={{ borderRadius: "999px" }}
                        />
                      </div>
                    </div>
                  </div>
                ) : profile ? (
                  <div style={{ display: "grid", gap: 10 }}>
                    <div
                      style={{ color: theme.colors.textBody, lineHeight: 1.7 }}
                    >
                      {profile.bio || "No bio yet."}
                    </div>

                    {Array.isArray(profile.portfolio_links) &&
                      profile.portfolio_links.length > 0 && (
                        <>
                          <SectionTitle style={{ marginTop: 8 }}>
                            <Link2 size={18} /> Portfolio
                          </SectionTitle>
                          <div
                            style={{
                              display: "flex",
                              gap: 10,
                              flexWrap: "wrap",
                            }}
                          >
                            {profile.portfolio_links.map((url, i) => (
                              <Chip
                                as="a"
                                key={i}
                                href={url}
                                target="_blank"
                                rel="noreferrer"
                              >
                                <Globe size={14} /> {url}
                              </Chip>
                            ))}
                          </div>
                        </>
                      )}
                  </div>
                ) : (
                  <Empty>Couldn’t load this profile.</Empty>
                )}
              </Card>
            )}

            {/* Tasks */}
            {tab === "tasks" && (
              <Card>
                <SectionTitle>
                  <Briefcase size={18} /> Posted Tasks
                </SectionTitle>
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "10px",
                    color: theme.colors.primaryText,
                    fontSize: "0.9rem",
                    marginBottom: "16px",
                    padding: "12px 16px",
                    background: theme.colors.primaryLight,
                    borderRadius: "10px",
                    border: `1px solid ${theme.colors.primary}20`,
                  }}
                >
                  <Info size={16} style={{ marginTop: "1px", flexShrink: 0 }} />
                  <span>
                    Tasks that {fullName || "this user"} has posted on the
                    platform looking for service providers.
                  </span>
                </div>

                <SortRow>
                  <div
                    style={{ color: theme.colors.textMuted, fontWeight: 700 }}
                  >
                    {isLoadingTasks ? (
                      <Skeleton h="16px" w="80px" />
                    ) : (
                      `${sortedTasks.length} tasks`
                    )}
                  </div>
                  <div
                    style={{ display: "flex", gap: 8, alignItems: "center" }}
                  >
                    <ArrowUpDown size={16} />
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                    >
                      <option value="date_desc">Newest</option>
                      <option value="date_asc">Oldest</option>
                      <option value="budget_desc">Budget: High to Low</option>
                      <option value="budget_asc">Budget: Low to High</option>
                    </select>
                  </div>
                </SortRow>

                {isLoadingTasks ? (
                  <List>
                    {[1, 2, 3].map((i) => (
                      <div
                        key={i}
                        style={{
                          border: "1px solid #e2e8f0",
                          borderRadius: "14px",
                          padding: "14px",
                          background: "#fff",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                            marginBottom: "8px",
                          }}
                        >
                          <Skeleton h="18px" w="200px" />
                          <div style={{ marginLeft: "auto" }}>
                            <Skeleton h="18px" w="60px" />
                          </div>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            gap: "8px",
                            flexWrap: "wrap",
                          }}
                        >
                          <Skeleton
                            h="24px"
                            w="80px"
                            style={{ borderRadius: "999px" }}
                          />
                          <Skeleton
                            h="24px"
                            w="100px"
                            style={{ borderRadius: "999px" }}
                          />
                          <Skeleton
                            h="24px"
                            w="70px"
                            style={{ borderRadius: "999px" }}
                          />
                        </div>
                      </div>
                    ))}
                  </List>
                ) : sortedTasks.length ? (
                  <List>
                    {sortedTasks.map((t) => (
                      <Item key={t.id} to={`/tasks/${t.id}`}>
                        <ItemTop>
                          <ItemTitle style={{ textTransform: "capitalize" }}>
                            {t.title}
                          </ItemTitle>
                          <Price>${t.budget}</Price>
                        </ItemTop>
                        <ItemMeta>
                          <Badge>
                            <MapPin size={14} />
                            {t.location ||
                              (t.task_type === "online"
                                ? "Remote"
                                : "Location not available")}
                          </Badge>
                          <Badge>
                            <Calendar size={14} />
                            {formatDistanceToNow(parseDate(t.created_at), {
                              addSuffix: true,
                            })}
                          </Badge>
                          {typeof t.distance_miles === "number" && (
                            <Badge>
                              <Navigation size={14} />
                              {t.distance_miles.toFixed(1)} mi
                            </Badge>
                          )}
                        </ItemMeta>
                      </Item>
                    ))}
                  </List>
                ) : (
                  <Empty>No tasks yet.</Empty>
                )}
              </Card>
            )}

            {/* Completed Work */}
            {tab === "completed" && (
              <Card>
                <SectionTitle>
                  <Star size={18} /> Completed Work
                </SectionTitle>
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "10px",
                    color: theme.colors.primaryText,
                    fontSize: "0.9rem",
                    marginBottom: "16px",
                    padding: "12px 16px",
                    background: theme.colors.primaryLight,
                    borderRadius: "10px",
                    border: `1px solid ${theme.colors.primary}20`,
                  }}
                >
                  <Info size={16} style={{ marginTop: "1px", flexShrink: 0 }} />
                  <span>
                    Tasks that {fullName || "this user"} has successfully
                    completed for other users. Shows the work they were hired to
                    do and the amount they were paid.
                  </span>
                </div>

                <SortRow>
                  <div
                    style={{ color: theme.colors.textMuted, fontWeight: 700 }}
                  >
                    {isLoadingCompletedTasks ? (
                      <Skeleton h="16px" w="100px" />
                    ) : (
                      `${sortedCompletedTasks.length} completed tasks`
                    )}
                  </div>
                  <div
                    style={{ display: "flex", gap: 8, alignItems: "center" }}
                  >
                    <ArrowUpDown size={16} />
                    <select
                      value={completedSortBy}
                      onChange={(e) => setCompletedSortBy(e.target.value)}
                    >
                      <option value="date_desc">Most Recent</option>
                      <option value="date_asc">Oldest First</option>
                    </select>
                  </div>
                </SortRow>

                {isLoadingCompletedTasks ? (
                  <List>
                    {[1, 2, 3].map((i) => (
                      <div
                        key={i}
                        style={{
                          border: "1px solid #e2e8f0",
                          borderRadius: "14px",
                          padding: "14px",
                          background: "#fff",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                            marginBottom: "8px",
                          }}
                        >
                          <Skeleton h="18px" w="200px" />
                          <div style={{ marginLeft: "auto" }}>
                            <Skeleton h="18px" w="60px" />
                          </div>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            gap: "8px",
                            flexWrap: "wrap",
                          }}
                        >
                          <Skeleton
                            h="24px"
                            w="80px"
                            style={{ borderRadius: "999px" }}
                          />
                          <Skeleton
                            h="24px"
                            w="100px"
                            style={{ borderRadius: "999px" }}
                          />
                          <Skeleton
                            h="24px"
                            w="70px"
                            style={{ borderRadius: "999px" }}
                          />
                        </div>
                      </div>
                    ))}
                  </List>
                ) : sortedCompletedTasks.length ? (
                  <List>
                    {sortedCompletedTasks.map((task) => (
                      <Item key={task.id} to={`/tasks/${task.id}`}>
                        <ItemTop>
                          <ItemTitle style={{ textTransform: "capitalize" }}>
                            {task.title}
                          </ItemTitle>
                          <Price>${task.offer_amount || task.budget}</Price>
                        </ItemTop>
                        <ItemMeta>
                          <Badge>
                            <MapPin size={14} />
                            {task.location || "Remote"}
                          </Badge>
                          <Badge>
                            <Calendar size={14} />
                            Completed{" "}
                            {formatDistanceToNow(
                              parseDate(task.completed_at || task.updated_at),
                              {
                                addSuffix: true,
                              }
                            )}
                          </Badge>
                          {task.poster && (
                            <Badge>
                              <Star size={14} />
                              For{" "}
                              <span style={{ textTransform: "capitalize" }}>
                                {task.poster.first_name} {task.poster.last_name}
                              </span>
                            </Badge>
                          )}
                        </ItemMeta>
                      </Item>
                    ))}
                  </List>
                ) : (
                  <Empty>No completed work yet.</Empty>
                )}
              </Card>
            )}
          </ContentGrid>
        </Container>
      </Page>
    </>
  );
};

const mapStateToProps = (state) => ({ userState: state });
export default connect(mapStateToProps)(UserProfilePage);
